import type {
  AppInfo,
  ContentReference,
  DeviceInfo,
  NewEvent,
  UserIdentity,
} from '@ilteducation/activity-stream-types';
import type { AdditionalContent } from '@ilteducation/content-registry-types';

export type BookInformation = {
  contentType: ContentReference['type'];
  id: string;
  legacyId?: number; // used for legacy Readlogs
  title?: string;
  authors?: string;
  coverUrl?: string;
  pages?: number;
  properties: {
    audioTracking: boolean;
    pictureInPicture: string[];
    frog: boolean;
    bookType?: 'audio-book' | 'book' | 'scrolled-book';
    additionalContent?: AdditionalContent;
    bookTalkContent?: {
      questions: boolean;
      difficultWords: boolean;
    }; // TODO - FIXME - specify type
  };
  languages: {
    mainLanguage: string;
    secondaryLanguage?: string;
    text: string[];
    audio: string[];
    video: string[];
  };
};

export type Api = {
  postActivityStreamEvents?: (events: NewEvent[], flush?: boolean) => void;
};

export type UserInformation = {
  userIdentity: UserIdentity;
  subscriptionType: string; // TODO - FIXME - specify type
  locale: string; // TODO - FIXME - specify available locales
};

export type AppContext = {
  deviceInfo: DeviceInfo;
  appInfo: AppInfo;
};

export type VideoSize = {
  w: number;
  h: number;
};

export type VideoPosition = {
  x?: number;
  y?: number;
};

export type TrackAudio = {
  clipBegin: number | null;
  clipEnd: number | null;
  nodeName: string;
  src: string;
  url: string;
  turnPageAt?: number;
};

export type TrackText = {
  nodeName: string;
  src: string;
  srcFile: string;
  srcFragmentId: string;
};

export type Track = {
  audio: TrackAudio;
  text: TrackText;
  cfi?: string;
};

export type PageFile = {
  cfi: string;
  displayed: object;
  href: string;
  index: number;
  location: number;
  percentage: number;
};

export type AdditionalStylesheets = {
  styles: string;
  key: string;
};

export type TextLanguageSelectedEvent = {
  languageCode: string;
  position?: string;
};

export type SkipByItem = {
  title: string;
  value: string;
  desc?: string;
  level?: string;
  icon?: string;
};

export type BasicFeature = {
  show: boolean;
};

export const NAVIGATION_ITEMS = {
  TABLE_OF_CONTENT: 'tableOfContent',
  PAGE_LIST: 'pageList',
  BOOKMARKS_LIST: 'bookmarksList',
  HIGHLIGHT_LIST: 'highlightsList',
  PLAYLIST: 'playlist',
} as const;

export type NavigationItem = (typeof NAVIGATION_ITEMS)[keyof typeof NAVIGATION_ITEMS];

export type ButtonsLocation = 'toolbar' | 'controls';

export type NavigationFeature = {
  location: ButtonsLocation;
  navigationItems?: NavigationItem[];
} & BasicFeature;

export type AnnotationFeature = {
  location: ButtonsLocation;
  annotationItems?: NavigationItem[];
} & BasicFeature;

export type TopBarFeature = {
  transparent?: boolean;
} & BasicFeature;

export type Features = {
  topBar?: TopBarFeature;
  navigation: NavigationFeature;
  annotation?: AnnotationFeature;
  contrastControls?: BasicFeature;
  lockScreen?: BasicFeature;
  goToStart?: BasicFeature;
  opaqueMenus?: boolean;
  dyslexicFontAvailable?: boolean;
  pageTurnAnimationEnabled?: boolean;
  pageSlideAnimationEnabled?: boolean;
  touchControlsEnabled?: boolean;
  showLastPosition?: boolean;
  audioNarration?: BasicFeature;
  audioTracking?: BasicFeature;
  automaticPageTurn?: BasicFeature & { valueByDefault?: boolean; forceDefault?: boolean };
  narrationSpeedChange?: BasicFeature;
  switchMode?: BasicFeature;
};

export type ViewerSize = {
  width: number;
  height: number;
};

export type GoToItem = {
  target: string | number;
  isGoingForwardsInBook: boolean;
};

export type BookViewMode = 'paginated' | 'continuous' | 'audio-only';

export type SwitchBookPlayerModeEvent = {
  position?: string; // EpubCFI
};

export type NavigationDirection = 'next' | 'prev';

export type FontAwesomeIconSizes =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'lg'
  | 'xl'
  | '2xl'
  | '1x'
  | '2x'
  | '3x'
  | '4x'
  | '5x'
  | '6x'
  | '7x'
  | '8x'
  | '9x'
  | '10x'
  | undefined;
